import React, { useEffect, useRef, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router-dom';

import Table from '../components/table';
import Button from '../components/buttons/baseButton';
import { DropBoxFileInterface } from '../types/dropBox.type';
import { ColumnDef, ActionColumn } from '../types/table.type';
import { PresignedUrl } from '../types/global';
import GetDropBoxFiles from '../services/adapters/dropBoxFiles';
import PostDropBoxDownload from '../services/adapters/dropBoxDownload';
import PostDropBoxDelete from '../services/adapters/dropBoxDelete';
import useDropBoxFileUpload from '../hooks/useDropBoxFileUpload';
import useRequestState from '../hooks/useRequestState';
import { formatDateTimeToString } from '../utils/formatters';
import { uploadAcceptAttribute, validateUploadFileName } from '../utils/uploadValidation';

export async function loader(): Promise<DropBoxFileInterface[]> {
  const adapter = new GetDropBoxFiles();
  return adapter.adaptResponse();
}

const scanRefreshIntervalMs = 10000;

export default function DropBox() {
  const files = useLoaderData<DropBoxFileInterface[]>();
  const revalidator = useRevalidator();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { isUploading, uploadError, uploadFile } = useDropBoxFileUpload(() => {
    void revalidator.revalidate();
  });
  const { error: downloadError, handleRequest: handleDownloadRequest } = useRequestState<PresignedUrl>();
  const { error: deleteError, handleRequest: handleDeleteRequest } = useRequestState<void>();
  const [fileTypeError, setFileTypeError] = useState<string | null>(null);

  const error = fileTypeError || uploadError || downloadError?.message || deleteError?.message;

  // Scans normally finish within a minute, so refresh the list while any
  // file is still waiting on its result
  const hasPendingScans = files.some((file) => file.scanStatus === 'PENDING');
  useEffect(() => {
    if (!hasPendingScans) return undefined;
    const refreshTimer = setInterval(() => {
      void revalidator.revalidate();
    }, scanRefreshIntervalMs);
    return () => clearInterval(refreshTimer);
  }, [hasPendingScans, revalidator]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateUploadFileName(file.name);
    setFileTypeError(validationError);
    if (!validationError) {
      await uploadFile(file);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (s3Key: string) => {
    const adapter = new PostDropBoxDownload();
    const result = await handleDownloadRequest(adapter.adaptResponse({ s3Key }));
    if (result?.presignedUrl) {
      window.open(result.presignedUrl, '_blank');
    }
  };

  const handleDelete = async (s3Key: string) => {
    const adapter = new PostDropBoxDelete();
    const result = await handleDeleteRequest(adapter.adaptResponse({ s3Key }));
    if (result !== null) {
      revalidator.revalidate();
    }
  };

  const columns: ColumnDef<DropBoxFileInterface>[] = [
    {
      key: 'fileName',
      header: 'File Name',
      sortable: false,
      headerClassName: 'cursor-default ',
    },
    {
      key: 'uploadedBy',
      header: 'Uploaded By',
      sortable: false,
      headerClassName: 'cursor-default',
    },
    {
      key: 'uploadedAt',
      header: 'Uploaded',
      sortable: false,
      headerClassName: 'cursor-default',
      render: (value: string) => formatDateTimeToString(value),
    },
    {
      key: 'expiresAt',
      header: 'Expires',
      sortable: false,
      headerClassName: 'cursor-default',
      render: (value: string) => formatDateTimeToString(value),
    },
  ];

  const actionColumns: ActionColumn<DropBoxFileInterface>[] = [
    {
      key: 'download',
      header: 'Download',
      render: (row: DropBoxFileInterface) => {
        const isExpired = new Date(row.expiresAt) < new Date();
        if (isExpired) {
          return <span className="text-xs text-pipgrey-dark italic">Expired, pending deletion</span>;
        }
        switch (row.scanStatus) {
          case 'PENDING':
            return <span className="text-xs text-pipgrey-dark italic">Scanning for viruses...</span>;
          case 'INFECTED':
            return <span className="text-xs text-red-600">Blocked: threat detected</span>;
          case 'ERROR':
            return <span className="text-xs text-red-600">Scan failed</span>;
          default:
            return (
              <div className="flex justify-center">
                <Button
                  id={`download-${row.s3Key}`}
                  label="Download"
                  className="pointer-events-auto button-green"
                  onClick={(e) => { e.stopPropagation(); handleDownload(row.s3Key); }}
                />
              </div>
            );
        }
      },
    },
    {
      key: 'delete',
      header: 'Delete',
      render: (row: DropBoxFileInterface) => (
        <div className="flex justify-center">
          <Button
            id={`delete-${row.s3Key}`}
            label="Delete"
            className="pointer-events-auto button-red"
            onClick={(e) => { e.stopPropagation(); handleDelete(row.s3Key); }}
          />
        </div>
      ),
    },
  ];

  return (
    <div id="drop-box-page" className="flex flex-col w-full p-4 overflow-auto">
      <div className="flex flex-row justify-between border-b-2 items-center">
        <h1 id="drop-box-header" className="m-2 p-4 text-header font-medium text-primary-dark">File Exchange</h1>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={uploadAcceptAttribute}
            onChange={handleFileChange}
          />
          <Button
            id="drop-box-upload-button"
            label={isUploading ? 'Uploading...' : 'Upload File'}
            className="button-green"
            onClick={() => fileInputRef.current?.click()}
            isDisabled={isUploading}
          />
        </div>
      </div>
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
          {error}
        </div>
      )}
      <p className="text-sm text-pipgrey-dark mt-4 mb-2">
        Files expire 24 hours after upload, and will be deleted within 48 hours
      </p>
      <Table
        data={files}
        columns={columns}
        actionColumns={actionColumns}
        title="Files"
        emptyMessage="No files uploaded yet"
        countLabel="files"
      />
    </div>
  );
}
